import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import TableContainerMin from "../../../Components/Common/TableContainer";
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import AddEditInstructionForm from "../component/AddEditInstructionForm";
import DetailInstruction from "../component/detail";
import { getInstruction, deleteInstruction } from "../service/instructionApi";
import { useTranslation } from "react-i18next";

const Instructions = () => {
  const { t } = useTranslation();
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Instructions | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const { isLoading, isError, error, data: instructions } = useQuery("instructions", getInstruction);

  const toggleAddEditModal = useCallback((instruction = null) => {
    setSelectedInstruction(instruction);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
  }, [isDetailModalOpen]);

  const handleEdit = (instruction) => {
    toggleAddEditModal(instruction);
  };
  
  const toggleDetailModal = useCallback((instruction = null) => {
    setSelectedInstruction(instruction);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
  }, [isAddEditModalOpen]);

  const handleDetail = (instruction) => {
    toggleDetailModal(instruction);
  };

  const handleDelete = async (instructionId) => {
    try {
      await deleteInstruction(instructionId);
      queryClient.invalidateQueries("instructions");
      toast.success(t("instruction.delete_success"));
    } catch (error) {
      console.error("Error deleting instruction:", error);
      toast.error(`${t("instruction.delete_error")}: ${error?.message || error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (instructionId) => {
    const instructionToDelete = instructions.find(instruction => instruction.id === instructionId);
    if (instructionToDelete) {
      setDeleteTargetId(instructionId);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
    {
      Header: t("instruction_list.avatar"),
      accessor: "Avatar",
      Cell: ({ row }) => (
        <div className="avatar"><span>{row.original.name.charAt(0).toUpperCase()}</span></div>
      ),
      filterable: false,
    },
    {
      Header: t("instruction.name"),
      accessor: "name",
      Cell: ({ row }) => (
        <div className="text-start">{row.original.name || t("instruction.no_name")}</div>
      ),
      filterable: false,
    },
    {
      Header: t("instruction.description"),
      accessor: "description",
      Cell: ({ row }) => (
        <div className="text-center">{row.original.description || t("instruction.no_description")}</div>
      ),
      filterable: false
    },
    {
      Header: t("instruction_list.actions"),
      accessor: "action",
      headerStyle: { textAlign: "center" },
      Cell: ({ row }) => (
        <div className=" actions-cell">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-detail">{t("instruction_list.view_details")}</Tooltip>}
          >
            <EyeOutlined style={{ color: '#1F5357', fontSize: '15px' }} onClick={() => handleDetail(row.original)} />
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-edit">{t("instruction_list.edit")}</Tooltip>}
          >
            <EditOutlined style={{ color: ' #FF9800', fontSize: '15px' }} onClick={() => handleEdit(row.original)} />
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-delete">{t("instruction_list.delete")}</Tooltip>}
          >
            <DeleteOutlined style={{ color: '#D32F2F', fontSize: '15px' }} onClick={() => openDeleteConfirmModal(row.original.id)} />
          </OverlayTrigger>
        </div>
      ),
      filterable: false,
    },
  ], [handleEdit, openDeleteConfirmModal, handleDetail]);

  const tabs = [t("instruction_list.all"), t("instruction_list.assigned"), t("instruction_list.unassigned"), t("instruction_list.archived")];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button
          key={tab}
          onClick={() => handleTabChange(tab)}
          className={`btn ${activeTab === tab ? 'active-tab' : 'btn-light'}`}
        >
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3"><strong>{t("instruction_list.list")}</strong></div>
      <button
        style={{
          padding: '5px 10px',
          borderRadius: '20px',
          backgroundColor: '#1F5357',
          color: 'white',
          border: 'none',
          fontSize: '15px',
          cursor: 'pointer',
          transition: 'background-color 0.3s',
        }}
        onClick={() => toggleAddEditModal(null)}
      >
        {t("instruction_list.create")}
      </button>
    </div>
  );

  const filteredInstructions = useMemo(() => {
    if (!instructions) return error?.message || [];
    switch (activeTab) {
      case t("instruction-list.assigned"):
        return instructions.filter(i => i.instructions === 'Assigned');
      case t("instruction_list.unassigned"):
        return instructions.filter(i => i.instructions === 'Unassigned');
      case t("instruction_list.archived"):
        return instructions.filter(i => i.instructions === 'Archived');
      default:
        return instructions;
    }
  }, [instructions, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page={t("instruction_list.list")} history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={filteredInstructions}
                    customPageSize={5}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder={t("global.search_placeholder")}
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      {t("global.loading")} <Spin />
                    </div>
                  )}
                  {filteredInstructions && filteredInstructions.length === 0 && !isLoading && (
                    <div>{t("global.no_data")}</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <AddEditInstructionForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedInstruction={selectedInstruction}
          />
          <DetailInstruction
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedInstruction={selectedInstruction}
          />
          <Modal
            title={t("instruction.delete_confirmation")}
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal} className="bordered-btn-delete">
                {t("global.no")}
              </Button>,
              <Button key="delete" type="danger" onClick={() => handleDelete(deleteTargetId)} className="bordered-btn">
                {t("global.yes")}
              </Button>,
            ]}
            width={400}
            style={{ top: 200 }}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Instructions;
